import { useEffect, useState } from "react";
import { api } from "./api";
import { Send, Clock, CheckCircle2, XCircle, AlertTriangle, X } from "lucide-react";

const AdminOutbox = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [resendOk, setResendOk] = useState(null);
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    const r = await api.listOutbound(status || undefined);
    setItems(r.data.items || []);
    setResendOk(r.data.resend_configured);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [status]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled send?")) return;
    await api.cancelOutbound(id);
    refresh();
  };

  const counts = items.reduce((acc, it) => { acc[it.status] = (acc[it.status]||0)+1; return acc; }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Outbox</h1>
        <p className="text-white/60 mt-1 text-sm">
          Approved emails, follow-up schedule, sent history. Autopilot progresses each lead through the cadence.
        </p>
      </div>

      {resendOk === false && (
        <div className="rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold">Simulation mode</div>
            <div className="mt-1 text-yellow-200/80">
              No Resend API key configured — emails are queued and processed on schedule but NOT delivered. Add <code>RESEND_API_KEY</code> to <code>backend/.env</code> to flip on real sending. Get one free at <a href="https://resend.com" className="underline" target="_blank" rel="noreferrer">resend.com</a> (100 emails/day free).
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["queued", "Queued", "bg-sky-500/10 text-sky-300"],
          ["scheduled", "Scheduled", "bg-yellow-500/10 text-yellow-300"],
          ["sent", "Sent", "bg-green-500/10 text-green-400"],
          ["failed", "Failed", "bg-red-500/10 text-red-400"],
          ["cancelled", "Cancelled", "bg-white/5 text-white/50"],
        ].map(([k, l, c]) => (
          <button key={k} onClick={() => setStatus(status === k ? "" : k)} className={`rounded-2xl bg-[#0f1512] border p-4 text-left transition ${status === k ? "border-[var(--tt-green)]" : "border-white/10 hover:border-white/20"}`}>
            <div className="text-2xl font-display font-bold">{counts[k] || 0}</div>
            <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold mt-2 ${c}`}>{l}</div>
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-[#0f1512] border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">Kind</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Subject</th>
              <th className="text-left px-4 py-3">Scheduled</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3"> </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-10 text-center text-white/40 text-sm">Nothing here yet. Approve an AI-drafted email on a lead to start.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-4 py-3">
                  <div className="font-semibold">{it.lead.company || "—"}</div>
                  <div className="text-xs text-white/50">{it.lead.contact_name} · {it.lead.email}</div>
                </td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-wider text-white/70">{it.kind}</span></td>
                <td className="px-4 py-3 hidden md:table-cell text-white/70">{it.subject || <span className="text-white/30 italic">Auto-generated at send</span>}</td>
                <td className="px-4 py-3 text-xs text-white/60">{new Date(it.scheduled_for).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                    it.status === "sent" ? "bg-green-500/10 text-green-400" :
                    it.status === "queued" ? "bg-sky-500/10 text-sky-300" :
                    it.status === "scheduled" ? "bg-yellow-500/10 text-yellow-300" :
                    it.status === "failed" ? "bg-red-500/10 text-red-400" :
                    "bg-white/5 text-white/50"
                  }`}>
                    {it.status === "sent" ? <CheckCircle2 className="h-3 w-3" /> : it.status === "failed" ? <XCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    {it.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-2 text-xs">
                    {(it.status === "queued" || it.status === "scheduled") && (
                      <button onClick={() => cancel(it.id)} className="text-white/50 hover:text-red-400 transition">Cancel</button>
                    )}
                    {it.subject && <button onClick={() => setPreview(it)} className="text-white/60 hover:text-white transition">View</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-2xl bg-[#0f1512] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">{preview.subject}</h3>
              <button onClick={() => setPreview(null)} className="text-white/50 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="text-xs text-white/50 mb-3">
              To: {preview.lead.contact_name} &lt;{preview.lead.email}&gt; · {preview.kind} · {preview.status}
            </div>
            <pre className="text-sm text-white/90 whitespace-pre-wrap font-sans bg-black/30 border border-white/10 rounded-lg p-4">{preview.body}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOutbox;
